export type UUID = string;

export enum CardSelectionOptions {
    GoldVisa = "GOLD_VISA",
    BlackVisa = "BLACK_VISA",
    Platinum = "PLATINUM_VISA",
    BlueVisa = "BLUE_VISA",
    TealVisa = "TEAL_VISA",
    PhotocardVisa = "PHOTOCARD_VISA",
    GoldMC = "GOLD_MC",
    BlackMC = "BLACK_MC",
    BlueMC = "BLUE_MC",
    TealMC = "TEAL_MC",
    PhotocardMC = "PHOTOCARD_MC",
}

export type FieldChangeInfo = {
    field: string;
    value: string | boolean;
    timestamp: string;
};

export enum ApplicationStatus {
    Approved = "approved",
    Duplicate = "duplicate",
    DuplicateDsn = "duplicate-dsn",
    ThankYou = "thank-you",
    Processing = "processing",
    Secured = "secured",
    SecuredAccepted = "secured-accepted",
}

export enum AddressKeys {
    AddressLine1 = "addressLine1",
    AddressLine2 = "addressLine2",
    City = "city",
    State = "state",
    Zipcode = "zipCode",
}

export enum AddressValidationKeys {
    AddressLine1 = "address.addressLine1",
    AddressLine2 = "address.addressLine2",
    City = "address.city",
    State = "address.state",
    Zipcode = "address.zipCode",
}

export const cardImageKey = "photoCardImage";
export enum AppKeys {
    Email = "email",
    CardSelection = "cardSelection",
    FirstName = "firstName",
    MiddleInitial = "middleInitial",
    LastName = "lastName",
    Address = "address",
    StudentStatus = "studentStatus",
    PrimaryPhone = "homePhone",
    DateOfBirth = "dob",
    SocialSecurity = "ssn",
    College = "collegeAttending",
    PaymentAccount = "paymentMethods",
    AccountTotal = "accountBalances",
    AnnualGrossIncome = "annualIncome",
    Residency = "citizen",
    AnnualObligation = "annualObligations",
    CheckingAccount = "ownCheckingAccount",
    SavingsAccount = "ownSavingsAccount",
    UrlCode = "urlCode",
    InviteCode = "invitationCode",
    ReferralCode = "referralCode",
}

export type ApplicationAddress = {
    [AddressKeys.AddressLine1]: string;
    [AddressKeys.AddressLine2]: string | null;
    [AddressKeys.City]: string;
    [AddressKeys.State]: string;
    [AddressKeys.Zipcode]: string;
};

export type Application = {
    [AppKeys.AccountTotal]: string;
    [AppKeys.Address]: ApplicationAddress;
    [AppKeys.AnnualGrossIncome]: string;
    [AppKeys.CardSelection]: string;
    [AppKeys.DateOfBirth]: string;
    [AppKeys.Email]: string;
    [AppKeys.FirstName]: string;
    [AppKeys.LastName]: string;
    [AppKeys.MiddleInitial]: string;
    [AppKeys.AnnualObligation]: string;
    [AppKeys.PaymentAccount]: string;
    [AppKeys.PrimaryPhone]: string;
    [AppKeys.Residency]: boolean | null;
    [AppKeys.SocialSecurity]: string;
    [AppKeys.College]: string;
    [AppKeys.StudentStatus]: string | null;
    [AppKeys.CheckingAccount]: boolean | null;
    [AppKeys.SavingsAccount]: boolean | null;
    [AppKeys.UrlCode]: string | null;
    [AppKeys.InviteCode]: string | null;
    [AppKeys.ReferralCode]: string | null;
};

export type AppValidation = {
    [AppKeys.AccountTotal]?: string[];
    [AddressValidationKeys.AddressLine1]?: string[];
    [AddressValidationKeys.AddressLine2]?: string[];
    [AddressValidationKeys.City]?: string[];
    [AddressValidationKeys.State]?: string[];
    [AddressValidationKeys.Zipcode]?: string[];
    [AppKeys.AnnualGrossIncome]?: string[];
    [AppKeys.CheckingAccount]?: string[];
    [AppKeys.DateOfBirth]?: string[];
    [AppKeys.Email]?: string[];
    [AppKeys.FirstName]?: string[];
    [AppKeys.LastName]?: string[];
    [AppKeys.MiddleInitial]?: string[];
    [AppKeys.AnnualObligation]?: string[];
    [AppKeys.PaymentAccount]?: string[];
    [AppKeys.PrimaryPhone]?: string[];
    [AppKeys.Residency]?: string[];
    [AppKeys.SavingsAccount]?: string[];
    [AppKeys.SocialSecurity]?: string[];
    [AppKeys.StudentStatus]?: string[];
    [AppKeys.College]?: string[];
    [AppKeys.CardSelection]?: string[];
    [AppKeys.InviteCode]?: string[];
    [AppKeys.ReferralCode]?: string | null;
};

export type ForgotInfo = {
    [AppKeys.DateOfBirth]: string;
    [AppKeys.FirstName]: string;
    [AppKeys.MiddleInitial]: string;
    [AppKeys.LastName]: string;
    [AddressKeys.Zipcode]: string;
};

export type ForgotInfoValidation = {
    [AppKeys.DateOfBirth]?: string[];
    [AppKeys.FirstName]?: string[];
    [AppKeys.MiddleInitial]?: string[];
    [AppKeys.LastName]?: string[];
    [AddressKeys.Zipcode]?: string[];
};

export enum ReviewKeys {
    Text = "text",
    Title = "title",
    Email = "email",
    InvitationCode = "invitationCode",
    Rating = "rating",
}

export type Review = {
    [ReviewKeys.Text]: string;
    [ReviewKeys.Title]: string;
    [ReviewKeys.Email]: string;
    [ReviewKeys.InvitationCode]: string;
    [ReviewKeys.Rating]: number;
};

export type ReviewValidation = {
    [ReviewKeys.Text]?: string[];
    [ReviewKeys.Title]?: string[];
    [ReviewKeys.Email]?: string[];
    [ReviewKeys.InvitationCode]?: string[];
    [ReviewKeys.Rating]?: string[];
};

export enum SecuredReminderType {
    Email = "email",
    SMS = "sms",
}

export enum SecuredReminderKeys {
    Type = "type",
    Email = "email",
    Phone = "phoneNumber",
    Date = "date",
    // TODO: confirm this key is correct
    Amount = "amount",
}

export type SecuredReminderInfo = {
    [SecuredReminderKeys.Type]: SecuredReminderType;
    [SecuredReminderKeys.Email]: string;
    [SecuredReminderKeys.Phone]: string;
    [SecuredReminderKeys.Date]: string;
    [SecuredReminderKeys.Amount]: number;
};

export enum SecuredDepositKeys {
    Amount = "amount",
    AccountNumber = "acctNumber",
    ConfirmAccountNumber = "confirmAccountNumber",
    AbaNumber = "abaNumber",
    CardNumber = "cardNumber",
    CardType = "cardCreditDebitType",
    CardExpirationDate = "cardExpDate",
    CardName = "cardName",
    PhoneNumber = "phoneNumber",
    Save = "save",
    Warrant = "warrant",
}

/* BankAccountInfo and CardAccountInfo used for organizing data for endpoints */
export type SecuredBankInfo = {
    [SecuredDepositKeys.AccountNumber]: string;
    [SecuredDepositKeys.AbaNumber]: string;
};

export type SecuredCardInfo = {
    [SecuredDepositKeys.CardNumber]: string;
    [SecuredDepositKeys.CardExpirationDate]: string;
    [SecuredDepositKeys.CardName]: string;
    [SecuredDepositKeys.CardType]: string;
    [SecuredDepositKeys.PhoneNumber]: string;
    [AddressKeys.AddressLine1]: string;
    [AddressKeys.AddressLine2]: string | null;
    [AddressKeys.City]: string;
    [AddressKeys.State]: string;
    [AddressKeys.Zipcode]: string;
};

export type SecuredDepositValidation = {
    [SecuredDepositKeys.Amount]?: string[];
    [SecuredDepositKeys.AccountNumber]?: string[];
    [SecuredDepositKeys.ConfirmAccountNumber]?: string[];
    [SecuredDepositKeys.AbaNumber]?: string[];
    [SecuredDepositKeys.CardNumber]?: string[];
    [SecuredDepositKeys.CardExpirationDate]?: string[];
    [SecuredDepositKeys.CardName]?: string[];
    [SecuredDepositKeys.CardType]?: string[];
    [SecuredDepositKeys.PhoneNumber]?: string[];
    [AddressValidationKeys.AddressLine1]?: string[];
    [AddressValidationKeys.AddressLine2]?: string[];
    [AddressValidationKeys.City]?: string[];
    [AddressValidationKeys.State]?: string[];
    [AddressValidationKeys.Zipcode]?: string[];
};

export enum LookupAppKeys {
    EmailToken = "emailToken",
    DateOfBirth = "dob",
    SsnLast4 = "ssnLast4",
}

export type LookupAppInfo = {
    [LookupAppKeys.EmailToken]: string;
    [LookupAppKeys.DateOfBirth]: string;
    [LookupAppKeys.SsnLast4]: string;
};

export type LookupAppValidation = {
    [LookupAppKeys.EmailToken]?: string[];
    [LookupAppKeys.SsnLast4]?: string[];
    [LookupAppKeys.DateOfBirth]?: string[];
};

/* Endpoint Data */

export type ErrorData = {
    visitId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any;
    url: string;
    lineNumber?: number;
    colNumber?: number;
    stack: string;
};

export enum InviteValidationStatus {
    active = "active",
    expired = "expired",
    tooManyAttempts = "too-many-attempts",
}

export type InviteValidation = {
    status: InviteValidationStatus;
    urlCode: string | null;
    applicationInfo: PALApplicationInfo | null;
};

export type PALApplicationInfo = {
    firstName: string;
    middleInitial: string | null;
    lastName: string;
    addressLine1: string;
    addressLine2: string | null;
    city: string;
    state: string;
    zipCode: string;
    bindingID: string;
    invitationCode: string;
    cardTypes: string[];
    attributes: {
        defaultCardType?: string;
        multiImageSlider?: boolean;
    };
};

export type ApplicationValidationResponse = {
    errors: AppValidation;
};

export type ApplicationValidate = {
    [AppKeys.AccountTotal]: number | null;
    [AppKeys.Address]: ApplicationAddress;
    [AppKeys.AnnualGrossIncome]: number | null;
    [AppKeys.CheckingAccount]: boolean;
    [AppKeys.DateOfBirth]: string;
    [AppKeys.Email]: string;
    [AppKeys.FirstName]: string;
    [AppKeys.LastName]: string;
    [AppKeys.MiddleInitial]?: string;
    [AppKeys.AnnualObligation]: number | null;
    [AppKeys.PaymentAccount]: string[] | null;
    [AppKeys.PrimaryPhone]: string;
    [AppKeys.Residency]: boolean;
    [AppKeys.SavingsAccount]: boolean;
    [AppKeys.SocialSecurity]: string;
    [AppKeys.StudentStatus]: string | null;
    [AppKeys.College]: string;
    [AppKeys.CardSelection]: string;
    [cardImageKey]: string | null;
    [AppKeys.InviteCode]: string | null;
    [AppKeys.ReferralCode]: string | null;
    [AppKeys.UrlCode]: string | null;
};

export type CounterOffer = {
    type: string;
    apr: number;
    annualFee: number;
    mpd: number | null;
    expDate: Date | null;
    depositMin: string;
    depositMax: string;
    depositStep: string;
};

export type ApplicationSubmitResponse = {
    status: ApplicationStatus;
    instantCard: boolean;
    counterOffer: CounterOffer | null;
    applicationId: string | null;
    creditLine: number | null;
    addressInfo: ApplicationAddress | null;
    firstName: string | null;
    middleInitial: string | null;
    lastName: string | null;
    econsent: boolean;
    homePhone: string | null;
    email: string | null;
};

export enum AppSecurityKeys {
    ApplicationId = "applicationID",
    MothersMaidenName = "mothersMaidenName",
    ContactName = "contactName",
    ContactRelationship = "contactRelationship",
    ContactAddress = "contactAddress",
    ContactHomePhone = "contactHomePhone",
    ContactPermission = "contactPermission",
}

export type AppSecurityValidation = {
    [AppSecurityKeys.ApplicationId]?: string[];
    [AppSecurityKeys.MothersMaidenName]?: string[];
    [AppSecurityKeys.ContactName]?: string[];
    [AppSecurityKeys.ContactRelationship]?: string[];
    [AppSecurityKeys.ContactAddress]?: string[];
    [AppSecurityKeys.ContactHomePhone]?: string[];
    [AppSecurityKeys.ContactPermission]?: string[];
};

export type InstantCard = {
    accountNumber: string;
    expirationDate: string;
    creditLimit: number;
    cvv: string;
};

export enum StudentStatus {
    HSEntering = "EC",
    CollegeStudent = "CS",
    NotAStudent = "NOTS",
}

export type ShortCode = {
    shortCode: string;
};

export type Referral = {
    referrerFirstName: string;
    recipientRewardAmount: string;
};

export type GetCardTypesResponse = {
    cardTypes: string[];
    attributes: {
        defaultCardType?: string;
        multiImageSlider?: boolean;
    };
};
